import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import FeaturedNewsCarousel from "@/src/components/news/featured-news/featured-news-carousel";
import { PostSkeleton } from "@/src/components/posts/loading-skeletons/post-skeleton";
import PostCard from "@/src/components/posts/post-card";
import CreatePostModal from "@/src/components/posts/post-creation-modal";
import PostDetailModal from "@/src/components/posts/post-detail-modal";
import ReactorModal from "@/src/components/posts/post-reactor-modal";
import { RightPanel } from "@/src/components/posts/right-panel/right-panel";
import ScButton from "@/src/components/ui-kit/button";
import PermissionDenied from "@/src/components/ui-kit/permission-denied";
import { usePermissions } from "@/src/hooks/use-permissions";
import ScSearchInput from "@/src/components/ui-kit/search-bar";
import { usePosts } from "@/src/hooks/features/posts/use-posts";
import { useResponsiveGap } from "@/src/hooks/use-responsive-gap";
import { useTheme } from "@/src/styles/theme-provider";
import { Post } from "@/src/models/post.model";
import { useRouter } from "expo-router";
import { AddCircle } from "iconsax-react-nativejs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FlatList,
  Image,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { ActivityIndicator } from "react-native-paper";

const Feeds = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { varStyle } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const gap = useResponsiveGap();

  const {
    displayedPosts: posts,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    search,
    setSearch,
    handleSearch,
    createPost,
    modalOpen,
    setModalOpen,
    editPost,
    setEditPost,
    viewerOpen,
    viewerPostId,
    handleCloseViewer,
    confirmDeleteOpen,
    handleDeleteConfirm,
    setConfirmDeleteOpen,
    setDeletePostId,
    handleSubmit,
    handleLoadMoreSearchResults,
    isSearchLoading,
    hasSearched,
    showReactorModal,
    setReactorModalVisibility,
    handleCloseReactorModal,
  } = usePosts(false);

  const { canCreate, canView } = usePermissions();
  const canCreatePost = canCreate("post");
  const canViewPost = canView("post");

  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [currentPost, setCurrentPost] = useState<Post>();

  // Early return if user cannot view posts
  if (!canViewPost) {
    return (
      <View style={varStyle} className={`flex-1 bg-obsidian ${gap}`}>
        <PermissionDenied />
      </View>
    );
  }

  return (
    <View
      style={varStyle}
      className={`flex-1 flex-row w-full bg-obsidian ${gap}`}
    >
      <ScrollView
        className="w-[100%] md:w-[68%]"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          flexGrow: 1,
          paddingBottom: isMobile ? 24 : 0,
        }}
      >
        <View className={isMobile ? "gap-3" : "gap-4"}>
          {/* Search + Create Post */}
          {isMobile ? (
            <ScSearchInput
              value={search}
              onChangeText={setSearch}
              handleSearch={handleSearch}
              placeholderKey="LABEL_SEARCH_FEEDS"
              surface="screen"
              containerClassName="rounded-2xl"
              onCreatePress={canCreatePost ? () => setModalOpen(true) : undefined}
            />
          ) : (
            <View className="flex-row rounded-2xl border-ash-3 justify-between border bg-chalk p-4">
              <ScSearchInput
                containerClassName="rounded-2xl w-[50%] bg-obsidian"
                placeholderKey="LABEL_SEARCH_FEEDS"
                value={search}
                handleSearch={handleSearch}
                onChangeText={setSearch}
              />
              {canCreatePost && (
                <ScButton
                  text="BTN_CREATE"
                  onPress={() => setModalOpen(true)}
                  icon={<AddCircle color="white" />}
                />
              )}
            </View>
          )}

          <FeaturedNewsCarousel />

          {/* Posts Section */}
          <View className="gap-4">
            {isLoading && [1, 2, 3].map((item) => <PostSkeleton key={item} />)}

            {isError && (
              <View className="items-center py-6 gap-2">
                <Image
                  source={require("@/src/assets/images/error.png")}
                  style={{ resizeMode: "contain", width: 300, height: 300 }}
                  className="w-16 h-16 rounded-full"
                  alt="error"
                />
                <Text className="text-gray-dark text-sm">{t("ERROR_LOADING_POST")}</Text>
                <ScButton mode="outlined" text="BTN_RETRY" onPress={() => refetch()} />
              </View>
            )}

            {!isLoading && !isError && (
              <FlatList
                data={posts}
                keyExtractor={(item) => item.ItemId}
                contentContainerStyle={{ gap: 16 }}
                ListHeaderComponent={
                  hasSearched && posts.length > 0 ? (
                    <View className="flex-row items-center gap-1 px-1 flex-wrap">
                      <Text className="text-sm text-gray-500">{t("LABEL_SEARCH_RESULTS_FOR")}</Text>
                      <Text className="text-sm font-semibold text-gray-800">{search}</Text>
                    </View>
                  ) : null
                }
                renderItem={({ item: post }) => (
                  <PostCard
                    key={post.ItemId}
                    post={post}
                    onReactorCount={() => {
                      setReactorModalVisibility(true);
                      setCurrentPost(post);
                    }}
                    onEdit={(post) => {
                      setEditPost(post);
                      setModalOpen(true);
                    }}
                    onDelete={(post) => {
                      setDeletePostId(post.ItemId);
                      setConfirmDeleteOpen(true);
                    }}
                  />
                )}
                onEndReachedThreshold={0.5}
                onEndReached={() => {
                  if (hasSearched) {
                    handleLoadMoreSearchResults();
                  } else if (hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                  }
                }}
                ListFooterComponent={
                  isFetchingNextPage ? <ActivityIndicator size="small" className="py-4" /> : null
                }
                ListEmptyComponent={
                  isSearchLoading ? (
                    <View className="items-center py-10">
                      <ActivityIndicator size="large" />
                    </View>
                  ) : hasSearched ? (
                    <View className="items-center py-10 gap-3">
                      <Image
                        source={require("@/src/assets/images/empty.png")}
                        style={{ width: 200, height: 200 }}
                        resizeMode="contain"
                      />
                      <Text className="text-gray-800 text-sm font-medium">{t("LABEL_NO_RESULTS_FOR")}</Text>
                      <Text className="text-gray-500 text-sm font-semibold">{search}</Text>
                      <Text className="text-gray-400 text-xs text-center px-8">
                        {t("LABEL_TRY_DIFFERENT_KEYWORDS")}
                      </Text>
                    </View>
                  ) : (
                    <View className="items-center py-6">
                      <Text className="text-gray-dark text-sm mb-4">{t("LABEL_EMPTY_POST")}</Text>
                      <Image
                        source={require("@/src/assets/images/empty.png")}
                        style={{ width: 300, height: 300 }}
                        resizeMode="contain"
                      />
                    </View>
                  )
                }
              />
            )}
          </View>
        </View>
      </ScrollView>

      {/* Right Panel */}
      <RightPanel
        selectedTag={selectedTag}
        onTagSelect={setSelectedTag}
        onLeaderboardSeeAll={() => router.push("/leaderboard")}
      />

      {/* Modals */}
      <CreatePostModal
        visible={modalOpen}
        post={editPost ?? undefined}
        onClose={() => {
          setModalOpen(false);
          setEditPost(null);
        }}
        onSubmit={handleSubmit}
        isPosting={createPost.isPending}
      />

      <PostDetailModal
        visible={viewerOpen}
        postId={viewerPostId as string}
        onClose={handleCloseViewer}
      />

      <ConfirmModal
        visible={confirmDeleteOpen}
        title="QUEST_CONFIRM_DELETE_POST"
        description="LABEL_THIS_ACTION_CANNOT_BE_UNDONE"
        confirmText="DELETE"
        cancelText="CANCEL"
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      <ReactorModal
        showModal={showReactorModal}
        onClose={handleCloseReactorModal}
        reactions={currentPost?.reactionCount ?? {}}
        postId={currentPost?.ItemId}
      />
    </View>
  );
};

export default Feeds;
